/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { DetailsRow, IColumn, IDetailsRowProps, IDetailsRowStyles } from '@fluentui/react/lib/DetailsList';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { getRandomNumber } from 'src/core/utils/randomValueGenerators';
import { QnaMakerSynonymsColumnRenderer } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerSynonymsColumnRenderer';
import { QnaMakerSynonymsUiStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerSynonymsUiStore';
import { QnaMakerSynonymSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';

type SynonymData = { id: number; alterations: string[] };

export type QnaMakerSynonymRowRendererProps = {
    onAddSynonym: (value: boolean) => void;
    onDismissChanges: (id: number) => void;
    onSubmitChanges: (newData: Partial<QnaMakerSynonymSwagger>) => void;
    rowProps?: IDetailsRowProps;
    columns?: IColumn[];
    synonymsBeingEditedId: number;
    synonymsStore: QnaMakerSynonymsUiStore;
};

const messages = defineMessages({
    submit: {
        id: 'qnaMaker.qnaMakerSynonymRowRenderer.submit',
        defaultMessage: 'Submit'
    },
    cancel: {
        id: 'qnaMaker.qnaMakerSynonymRowRenderer.cancel',
        defaultMessage: 'Cancel'
    }
});

const localTheme = getScopedTheme('QnaMakerSynonymRowRenderer');
const { styleComponent } = getStylistV2('QnaMakerSynonymRowRenderer');

const RowContainer = styleComponent<IStackProps>(Stack)(
    'RowContainer',
    (theme, cssVars) => ({
        borderBottom: `${theme.focussingColor} solid 1px`,
        backgroundColor: cssVars.defaultBackground,
        $nest: {
            '&:hover': { backgroundColor: cssVars.hoveringBackground }
        }
    }),
    {
        cssVars: { hoveringBackground: '', defaultBackground: '' },
        cssVarsSetter: ({ isSelected, isInAddMode }: { isSelected: boolean; isInAddMode: boolean }) => ({
            hoveringBackground: `${isSelected ? localTheme.focussingColor : localTheme.hoveringColor}`,
            defaultBackground: `${isInAddMode ? localTheme.focussingColor : isSelected ? localTheme.hoveringColor : 'unset'}`
        })
    }
);

const detailsRowStyles = classNamesFunction<IDetailsRowProps, IDetailsRowStyles>()({
    root: {
        borderBottom: 'none',
        backgroundColor: 'transparent !important'
    },
    cell: {
        overflow: 'visible'
    },
    check: {
        cursor: 'pointer !important'
    }
});

/**
 * A component responsible for rendering the table row in
 * three different views:
 * 1. Regular view
 * 2. Edit mode view
 * 3. Add new Synonym view
 */
export const QnaMakerSynonymRowRenderer = (props: QnaMakerSynonymRowRendererProps) => {
    const { rowProps, onAddSynonym, onSubmitChanges, onDismissChanges, synonymsBeingEditedId, synonymsStore } = props;
    const { localize } = useLocalization();

    const item: SynonymData = rowProps?.item as SynonymData;
    const isInAddMode: boolean = !item;
    const isInEditMode: boolean = synonymsBeingEditedId === item?.id;
    const isCurrentItemSelected = rowProps?.selection.getSelectedIndices().includes(rowProps.itemIndex);

    const defaultItem: SynonymData = {
        id: getRandomNumber(),
        alterations: []
    };

    const [synonymData, updateSynonymData] = useUpdateState<SynonymData>(() => {
        if (isInEditMode) {
            return synonymsStore.getSynonymsBeingEdited(item?.id);
        }
        return item || defaultItem;
    });
    const [hasSynonymDataBeenModified, setHasSynonymDataBeenModified] = useState<boolean>(false);

    React.useEffect(() => {
        if (item) {
            if (isInEditMode) {
                updateSynonymData(synonymsStore.getSynonymsBeingEdited(item.id));
            } else {
                updateSynonymData(item);
            }
        }
    }, [item, isInEditMode]);

    React.useEffect(() => setHasSynonymDataBeenModified(false), [isInEditMode]);

    const onRenderSynonymsColumnInAddOrEditModes = () => (
        <QnaMakerSynonymsColumnRenderer
            isInEditMode
            synonyms={synonymData.alterations}
            onEditSynonyms={synonyms => {
                if (isInAddMode) {
                    onAddSynonym(synonyms.length > 0);
                }
                updateSynonymData({ ...synonymData, alterations: synonyms });
                setHasSynonymDataBeenModified(true);
            }}
        />
    );

    const editModeColumns = rowProps?.columns.map(column => ({
        ...column,
        onRender: onRenderSynonymsColumnInAddOrEditModes
    }));

    const computeSubmitButtonDisabledState = () => !hasSynonymDataBeenModified;

    const onSubmit = () => {
        if (isInAddMode) {
            onAddSynonym(false);
        }
        onSubmitChanges({ id: synonymData.id, alterations: synonymData.alterations });
    };

    const onDismiss = () => {
        if (isInEditMode) {
            synonymsStore.clearSynonymsBeingEditedId();
        }
        if (isInAddMode) {
            onAddSynonym(false);
        }
        updateSynonymData(item);
        onDismissChanges(item?.id);
    };

    return (
        <RowContainer
            id={`synonym_${synonymData?.id}`}
            customProps={{ isSelected: isCurrentItemSelected, isInAddMode }}
            tokens={{ childrenGap: 32 }}>
            {isInAddMode ? (
                <DetailsRow item={{}} itemIndex={-1} columns={editModeColumns} styles={detailsRowStyles} />
            ) : isInEditMode ? (
                <DetailsRow {...rowProps} columns={editModeColumns} styles={detailsRowStyles} />
            ) : (
                <DetailsRow {...rowProps} styles={detailsRowStyles} data-selection-disabled />
            )}

            <Stack horizontal verticalAlign="baseline" verticalFill tokens={{ childrenGap: 16, padding: '0 10px 20px 60px' }}>
                <Stack horizontal verticalAlign="center" styles={{ root: { width: '100%' } }}>
                    {(isInEditMode || isInAddMode) && (
                        <Stack horizontal verticalAlign="center" horizontalAlign="space-between" grow>
                            &nbsp;
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                                <PrimaryButton
                                    data-automation-id="primaryButton"
                                    text={localize(messages.submit)}
                                    disabled={computeSubmitButtonDisabledState()}
                                    onClick={onSubmit}
                                    styles={{ root: { height: 28 } }}
                                />
                                <DefaultButton text={localize(messages.cancel)} onClick={onDismiss} styles={{ root: { height: 28 } }} />
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </RowContainer>
    );
};
